import React, { useState } from 'react';
import {
    TrendingUp,
    Building2,
    Store,
    ChevronRight,
    Check,
    AlertCircle,
    DollarSign,
    Percent,
    ArrowUp,
    ArrowDown,
    Info,
    Layers,
    Award,
    Home,
    ChevronLeft,
    Flame,
    Car
} from 'lucide-react';

// Based on actual API response structure
interface CostAnalysis {
    base_cost_per_sqft: number;
    buildable_area_sqft: number;
    buildable_area_sqm: number;
    cost_multiplier: number;
    cost_per_sqft: number;
    cost_per_sqm: number;
    fsi_utilization: string;
    requires_approval: boolean;
    scenario_name: string;
    total_construction_cost: number;
}

interface FloorPlate {
    effective_buildable_area: number;
    reduced_floor_plate_sqm: number;
    reduction_applied: boolean;
    reduction_percentage: number;
    total_floors: number;
    typical_floor_plate_sqm: number;
}

interface RevenuePotential {
    construction_cost: number;
    estimated_sale_rate_per_sqft?: number;
    estimated_rent_per_sqft_monthly?: number;
    gross_margin?: number;
    gross_revenue?: number;
    saleable_area_sqft?: number;
    rentable_area_sqft?: number;
    annual_rental_income?: number;
    cap_rate?: number;
}

interface FSIUtilizationScenario {
    cost_analysis: CostAnalysis;
    description: string;
    floor_plate: FloorPlate;
    fsi_utilization: string;
    fsi_utilization_id: string;
    requires_approval: boolean;
    revenue_potential: RevenuePotential;
}

interface Requirements {
    basement_levels?: number;
    max_floors: number | null;
    min_area_sqft: number;
    requires_fire_safety: boolean;
    requires_lift: boolean;
    requires_tall_building_policy?: boolean;
}

interface BestScenario {
    construction_cost: number;
    fsi_utilization: string;
    gross_revenue: number;
    reason: string;
}

interface DevelopmentScenario {
    best_scenario?: BestScenario;
    description: string;
    fsi_utilization_scenarios: FSIUtilizationScenario[];
    requirements: Requirements;
    scenario_id: string;
    scenario_name: string;
}

interface ZoneFSIConfig {
    base_fsi: number;
    max_paid_fsi: number;
    tdr_allowed: boolean;
    tdr_max_additional?: number;
    total_max_fsi: number;
}

interface PlotAnalysis {
    estimated_floors: number;
    plot_area_sqft: number;
    plot_area_sqm: number;
    road_width_m: number;
    total_buildable_sqm: number;
    zone_code: string;
}

interface Summary {
    best_commercial?: { fsi_utilization: string; margin: number; type: string };
    best_residential?: { fsi_utilization: string; margin: number; type: string };
    total_commercial_options: number;
    total_residential_options: number;
    zone_allows_tdr: boolean;
}

interface DevelopmentScenariosData {
    commercial_scenarios: DevelopmentScenario[];
    fsi_utilization_types?: any[];
    notes?: string[];
    plot_analysis: PlotAnalysis;
    recommendations?: string[];
    residential_scenarios: DevelopmentScenario[];
    summary: Summary;
    zone_fsi_config: ZoneFSIConfig;
}

interface DevelopmentScenariosExplorerProps {
    data: DevelopmentScenariosData | any;
    onScenarioSelect?: (scenario: DevelopmentScenario, fsiStrategy: FSIUtilizationScenario) => void;
}

const DevelopmentScenariosExplorer: React.FC<DevelopmentScenariosExplorerProps> = ({ data, onScenarioSelect }) => {
    const [activeTab, setActiveTab] = useState<'residential' | 'commercial'>('residential');
    const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
    const [selectedFSI, setSelectedFSI] = useState<string | null>(null);
    const [compareMode, setCompareMode] = useState(false);
    const [compareItems, setCompareItems] = useState<string[]>([]);

    const formatCurrency = (num: number) => {
        if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
        if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
        return `₹${new Intl.NumberFormat('en-IN').format(Math.round(num))}`;
    };

    const formatNumber = (num: number) => new Intl.NumberFormat('en-IN').format(Math.round(num));

    const getScenarioIcon = (id: string) => {
        if (id.includes('retail') || id.includes('commercial')) return <Store className="w-5 h-5" />;
        if (id.includes('bungalow')) return <Home className="w-5 h-5" />;
        if (id.includes('tall')) return <Layers className="w-5 h-5" />;
        return <Building2 className="w-5 h-5" />;
    };

    const getMarginColor = (margin: number) => {
        if (margin >= 35) return 'text-emerald-600';
        if (margin >= 25) return 'text-blue-600';
        if (margin >= 15) return 'text-yellow-600';
        if (margin >= 0) return 'text-orange-600';
        return 'text-red-600';
    };

    const getMarginBgColor = (margin: number) => {
        if (margin >= 35) return 'bg-emerald-50 border-emerald-200';
        if (margin >= 25) return 'bg-blue-50 border-blue-200';
        if (margin >= 15) return 'bg-yellow-50 border-yellow-200';
        if (margin >= 0) return 'bg-orange-50 border-orange-200';
        return 'bg-red-50 border-red-200';
    };

    const scenarios = activeTab === 'residential'
        ? data.residential_scenarios || []
        : data.commercial_scenarios || [];

    const currentScenario = scenarios.find((s: DevelopmentScenario) => s.scenario_id === selectedScenario);
    const currentFSI = currentScenario?.fsi_utilization_scenarios.find((f: FSIUtilizationScenario) => f.fsi_utilization_id === selectedFSI);

    // Extract margin from "Best margin: XX%" or "Best cap rate: XX%"
    const extractMetric = (reason: string): number => {
        const match = reason.match(/([\d.]+)%/);
        return match ? parseFloat(match[1]) : 0;
    };

    return (
        <div className="space-y-4">
            {/* Best Options Highlight */}
            <div className="grid grid-cols-2 gap-3">
                {data.summary?.best_residential && (
                    <button
                        onClick={() => {
                            setActiveTab('residential');
                            const scenario = data.residential_scenarios?.find(
                                (s: DevelopmentScenario) => s.scenario_name === data.summary.best_residential?.type
                            );
                            if (scenario) {
                                setSelectedScenario(scenario.scenario_id);
                            }
                        }}
                        className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-3 text-white text-left hover:from-emerald-600 hover:to-teal-700 transition-colors"
                    >
                        <div className="flex items-center gap-1 mb-1">
                            <Award className="w-4 h-4" />
                            <span className="text-xs font-medium opacity-90">Best Residential</span>
                        </div>
                        <div className="font-bold text-sm truncate">{data.summary.best_residential.type}</div>
                        <div className="text-xs mt-1 opacity-80">
                            {data.summary.best_residential.margin}% margin
                        </div>
                    </button>
                )}
                {data.summary?.best_commercial && (
                    <button
                        onClick={() => {
                            setActiveTab('commercial');
                            const scenario = data.commercial_scenarios?.find(
                                (s: DevelopmentScenario) => s.scenario_name === data.summary.best_commercial?.type
                            );
                            if (scenario) {
                                setSelectedScenario(scenario.scenario_id);
                            }
                        }}
                        className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-3 text-white text-left hover:from-indigo-600 hover:to-purple-700 transition-colors"
                    >
                        <div className="flex items-center gap-1 mb-1">
                            <Award className="w-4 h-4" />
                            <span className="text-xs font-medium opacity-90">Best Commercial</span>
                        </div>
                        <div className="font-bold text-sm truncate">{data.summary.best_commercial.type}</div>
                        <div className="text-xs mt-1 opacity-80">
                            {data.summary.best_commercial.margin}% cap rate
                        </div>
                    </button>
                )}
            </div>

            {/* Recommendations */}
            {data.recommendations && data.recommendations.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200">
                    <h4 className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Analysis Insights
                    </h4>
                    <ul className="space-y-1">
                        {data.recommendations.slice(0, 4).map((rec: string, i: number) => (
                            <li key={i} className="text-xs text-amber-700">{rec}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Tab Selector */}
            <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                    onClick={() => { setActiveTab('residential'); setSelectedScenario(null); setSelectedFSI(null); }}
                    className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${activeTab === 'residential'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Building2 className="w-4 h-4" />
                    Residential ({data.summary?.total_residential_options || 0})
                </button>
                <button
                    onClick={() => { setActiveTab('commercial'); setSelectedScenario(null); setSelectedFSI(null); }}
                    className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${activeTab === 'commercial'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Store className="w-4 h-4" />
                    Commercial ({data.summary?.total_commercial_options || 0})
                </button>
            </div>

            {/* Zone FSI Config Quick View */}
            <div className="bg-gray-50 rounded-lg p-3 flex flex-wrap gap-3 text-sm">
                <div>
                    <span className="text-gray-500">Base FSI:</span>
                    <span className="ml-1 font-semibold text-gray-900">{data.zone_fsi_config?.base_fsi}</span>
                </div>
                <div>
                    <span className="text-gray-500">Max FSI:</span>
                    <span className="ml-1 font-semibold text-gray-900">{data.zone_fsi_config?.total_max_fsi}</span>
                </div>
                <div>
                    <span className="text-gray-500">Floors:</span>
                    <span className="ml-1 font-semibold text-gray-900">G+{(data.plot_analysis?.estimated_floors || 1) - 1}</span>
                </div>
                {data.zone_fsi_config?.tdr_allowed && (
                    <div className="text-emerald-600 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        TDR +{data.zone_fsi_config.tdr_max_additional}
                    </div>
                )}
            </div>

            {/* Scenario List */}
            {!selectedScenario ? (
                <div className="space-y-2">
                    {scenarios.map((scenario: DevelopmentScenario) => {
                        const bestFSI = scenario.best_scenario;
                        const metric = bestFSI ? extractMetric(bestFSI.reason) : 0;
                        const isResidential = activeTab === 'residential';

                        return (
                            <button
                                key={scenario.scenario_id}
                                onClick={() => setSelectedScenario(scenario.scenario_id)}
                                className="w-full text-left bg-white hover:bg-gray-50 border border-gray-200 rounded-xl p-4 transition-colors group"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className={`p-2 rounded-lg ${isResidential ? 'bg-emerald-50 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}`}>
                                            {getScenarioIcon(scenario.scenario_id)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-gray-900 truncate">{scenario.scenario_name}</h4>
                                            <p className="text-sm text-gray-500 mt-0.5 truncate">{scenario.description}</p>

                                            {/* Requirements badges */}
                                            <div className="flex gap-1.5 mt-2 flex-wrap">
                                                {scenario.requirements?.max_floors && (
                                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded flex items-center gap-0.5">
                                                        <Layers className="w-2.5 h-2.5" />
                                                        Max G+{scenario.requirements.max_floors - 1}
                                                    </span>
                                                )}
                                                {scenario.requirements?.requires_lift && (
                                                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">
                                                        Lift
                                                    </span>
                                                )}
                                                {scenario.requirements?.requires_fire_safety && (
                                                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded flex items-center gap-0.5">
                                                        <Flame className="w-2.5 h-2.5" />
                                                        Fire safety
                                                    </span>
                                                )}
                                                {scenario.requirements?.basement_levels && scenario.requirements.basement_levels > 0 && (
                                                    <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded flex items-center gap-0.5">
                                                        <Car className="w-2.5 h-2.5" />
                                                        {scenario.requirements.basement_levels}B
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 ml-2">
                                        <div className={`text-right px-3 py-1.5 rounded-lg border ${getMarginBgColor(metric)}`}>
                                            <div className={`text-lg font-bold ${getMarginColor(metric)}`}>
                                                {metric.toFixed(1)}%
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {isResidential ? 'margin' : 'cap rate'}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : (
                /* FSI Strategy Selection */
                <div className="space-y-3">
                    {/* Back button */}
                    <button
                        onClick={() => { setSelectedScenario(null); setSelectedFSI(null); }}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to all {activeTab} options
                    </button>

                    {/* Current scenario header */}
                    <div className={`rounded-xl p-4 ${activeTab === 'residential' ? 'bg-emerald-50 border border-emerald-200' : 'bg-indigo-50 border border-indigo-200'}`}>
                        <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${activeTab === 'residential' ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-100 text-indigo-600'}`}>
                                {getScenarioIcon(currentScenario?.scenario_id || '')}
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900">{currentScenario?.scenario_name}</h4>
                                <p className="text-sm text-gray-600 mt-0.5">{currentScenario?.description}</p>
                            </div>
                        </div>
                    </div>

                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Choose FSI Strategy:
                    </h4>

                    {/* FSI Strategies */}
                    <div className="space-y-2">
                        {currentScenario?.fsi_utilization_scenarios.map((fsi: FSIUtilizationScenario) => {
                            const isSelected = selectedFSI === fsi.fsi_utilization_id;
                            const isBest = currentScenario.best_scenario?.fsi_utilization === fsi.fsi_utilization;
                            const isResidential = activeTab === 'residential';
                            const metric = isResidential
                                ? fsi.revenue_potential.gross_margin
                                : fsi.revenue_potential.cap_rate;

                            return (
                                <div key={fsi.fsi_utilization_id}>
                                    <button
                                        onClick={() => {
                                            setSelectedFSI(isSelected ? null : fsi.fsi_utilization_id);
                                            if (!isSelected && currentScenario) {
                                                onScenarioSelect?.(currentScenario, fsi);
                                            }
                                        }}
                                        className={`w-full text-left border rounded-xl p-4 transition-all ${isSelected
                                            ? 'border-blue-500 bg-blue-50 shadow-md'
                                            : isBest
                                                ? 'border-emerald-300 bg-emerald-50/50 hover:border-emerald-400'
                                                : 'border-gray-200 hover:border-gray-300 bg-white'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h5 className="font-medium text-gray-900">{fsi.fsi_utilization}</h5>
                                                    {isBest && (
                                                        <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                                            <Award className="w-3 h-3" /> Best
                                                        </span>
                                                    )}
                                                    {fsi.requires_approval && (
                                                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded flex items-center gap-1">
                                                            <AlertCircle className="w-3 h-3" /> Approval
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 mt-1">{fsi.description}</p>

                                                {/* Cost multiplier indicator */}
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Cost multiplier: <span className={`font-medium ${fsi.cost_analysis.cost_multiplier > 1 ? 'text-orange-600' : 'text-gray-700'}`}>
                                                        {fsi.cost_analysis.cost_multiplier}x
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="text-right ml-4">
                                                <div className={`text-xl font-bold ${getMarginColor(metric || 0)}`}>
                                                    {metric?.toFixed(1)}%
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {isResidential ? 'Margin' : 'Cap Rate'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expanded details */}
                                        {isSelected && (
                                            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 animate-fadeIn">
                                                {/* Key Metrics */}
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                                                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                                            <ArrowDown className="w-3 h-3 text-red-400" />
                                                            Construction Cost
                                                        </div>
                                                        <div className="font-bold text-gray-900">
                                                            {formatCurrency(fsi.cost_analysis.total_construction_cost)}
                                                        </div>
                                                        <div className="text-xs text-gray-400">
                                                            ₹{formatNumber(fsi.cost_analysis.cost_per_sqft)}/sqft
                                                        </div>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                                                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                                            <ArrowUp className="w-3 h-3 text-emerald-400" />
                                                            {isResidential ? 'Gross Revenue' : 'Annual Rental'}
                                                        </div>
                                                        <div className="font-bold text-emerald-600">
                                                            {formatCurrency(isResidential
                                                                ? (fsi.revenue_potential.gross_revenue || 0)
                                                                : (fsi.revenue_potential.annual_rental_income || 0)
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-400">
                                                            ₹{formatNumber(isResidential
                                                                ? (fsi.revenue_potential.estimated_sale_rate_per_sqft || 0)
                                                                : ((fsi.revenue_potential.estimated_rent_per_sqft_monthly || 0) * 12)
                                                            )}/{isResidential ? 'sqft' : 'sqft/yr'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Area & Floor Details */}
                                                <div className="bg-white rounded-lg p-3 border border-gray-100">
                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Buildable Area:</span>
                                                            <span className="font-medium">{formatNumber(fsi.cost_analysis.buildable_area_sqft)} sqft</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Saleable:</span>
                                                            <span className="font-medium">
                                                                {formatNumber(isResidential
                                                                    ? (fsi.revenue_potential.saleable_area_sqft || 0)
                                                                    : (fsi.revenue_potential.rentable_area_sqft || 0)
                                                                )} sqft
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Floors:</span>
                                                            <span className="font-medium">G+{(fsi.floor_plate.total_floors || 1) - 1}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-500">Floor Plate:</span>
                                                            <span className="font-medium">{formatNumber(fsi.floor_plate.typical_floor_plate_sqm)} m²</span>
                                                        </div>
                                                    </div>
                                                    {fsi.floor_plate.reduction_applied && (
                                                        <div className="mt-2 text-xs text-yellow-600 bg-yellow-50 rounded px-2 py-1">
                                                            ⚠️ {fsi.floor_plate.reduction_percentage}% tall building reduction applied
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Profit Summary */}
                                                <div className={`rounded-lg p-3 ${getMarginBgColor(metric || 0)} border`}>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-700">Net Profit Estimate:</span>
                                                        <span className={`text-lg font-bold ${getMarginColor(metric || 0)}`}>
                                                            {formatCurrency(
                                                                (isResidential
                                                                    ? (fsi.revenue_potential.gross_revenue || 0)
                                                                    : (fsi.revenue_potential.annual_rental_income || 0) * 10)
                                                                - fsi.cost_analysis.total_construction_cost
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {isResidential
                                                            ? 'Revenue - Construction Cost'
                                                            : '10-Year Rental - Construction Cost'}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DevelopmentScenariosExplorer;
